import config from 'config';

import { SSOStrategyType } from '@/hooks/useAppConfig';

import { AuthErrorCodes } from './errorMessage';

const pem = config.publicEncryptionKey;

export const isPasswordValid = (password?: string) => {
  if (!password) {
    return AuthErrorCodes.CANT_BE_EMPTY;
  }

  return getPasswordRules(password).find((rule) => rule.isFailing)?.message ?? null;
};

export const getPasswordRules = (password: string = '') => {
  const noNumber = !/\d/.test(password);
  const noUpperCase = !/[A-Z]/.test(password);
  const noLowerCase = !/[a-z]/.test(password);
  const noSpecialChar = !/[^\w*]/.test(password);

  return [
    { isFailing: password.length < 8, message: AuthErrorCodes.MUST_BE_8_CHAR },
    { isFailing: noSpecialChar, message: AuthErrorCodes.NO_SPECIAL_CHAR },
    { isFailing: noNumber, message: AuthErrorCodes.NO_NUMBER },
    { isFailing: noUpperCase, message: AuthErrorCodes.NO_UPPER_CASE },
    { isFailing: noLowerCase, message: AuthErrorCodes.NO_LOWER_CASE },
  ];
};

const convertPemToBinary = async () => {
  const base64 = pem!
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s/g, '');

  const binary = Uint8Array.from(window.atob(base64), (c) => c.charCodeAt(0)).buffer;
  return binary;
};

export const getEncryptedPassword = async (password: string) => {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const publicKey = await convertPemToBinary();
    const importedKey = await window.crypto.subtle.importKey(
      'spki',
      publicKey,
      {
        name: 'RSA-OAEP',
        hash: { name: 'SHA-256' },
      },
      false,
      ['encrypt'],
    );
    const encryptedData = await window.crypto.subtle.encrypt(
      {
        name: 'RSA-OAEP',
      },
      importedKey,
      data,
    );
    return Array.from(new Uint8Array(encryptedData))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    console.error('Error encrypting password');
    throw error;
  }
};

export const getProcessedPassword = async ({
  strategyMethods,
  password,
}: {
  strategyMethods: SSOStrategyType[] | undefined;
  password: string;
}) => {
  try {
    const localStrategyMethod = strategyMethods?.find((method) => method.provider === 'LOCAL');
    const shouldEncryptPassword = localStrategyMethod?.useEncryptedPassword ?? false;

    const processedPassword = shouldEncryptPassword ? getEncryptedPassword(password) : password;
    return processedPassword;
  } catch (error) {
    console.log('Error while getting processed password: ', error);
    return password; // do not throw error from here, return actual password instead
  }
};
